import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Form,
  CardTitle,
  Field,
} from '@cordata/buffet';
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
} from 'reactstrap';
import * as Yup from 'yup';

interface SendgridEventAttributes {
  orgUid: string;
  divisionUid: string | null;
  outboundEmailUid: string | null;
  email: string;
  sendgridTimestamp: string;
  smtpId: string;
  event: string;
  sgEventId: string;
  sgMessageId: string;
  response: string | null;
  attempt: number | null;
  reason: string;
  status: string;
  useragent: string | null;
  ip: string;
  url: string | null;
  customArgs: string | null;
  asmGroupId: string | null;
  rawPayload: string;
}

interface SendgridEvent {
  id: string;
  type: string;
  attributes: SendgridEventAttributes;
  links: {
    self: string;
  };
  'info-redacted': boolean;
  relationships: {
    'outbound-email': {
      related: string;
    };
    division: {
      related: string;
    };
    org: {
      related: string;
    };
  };
}

interface SendgridEventFormProps {
  event?: SendgridEvent;
}

const DEFAULT_VALUES = {
  id: '',
  type: '',
  attributes: {
    orgUid: '',
    divisionUid: null,
    outboundEmailUid: null,
    email: '',
    sendgridTimestamp: '',
    smtpId: '',
    event: '',
    sgEventId: '',
    sgMessageId: '',
    response: null,
    attempt: null,
    reason: '',
    status: '',
    useragent: null,
    ip: '',
    url: null,
    customArgs: null,
    asmGroupId: null,
    rawPayload: '',
  },
  links: {
    self: '',
  },
  'info-redacted': false,
  relationships: {
    'outbound-email': {
      related: '',
    },
    division: {
      related: '',
    },
    org: {
      related: '',
    },
  },
};

const formatJson = (jsonString: string | null) => {
  if (!jsonString) return 'N/A';
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return jsonString;
  }
};

const formatTimestamp = (timestamp: string) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleString();
};

export const getBadgeClass = (status) => {
  switch (status) {
    // Statuses for outbound emails (set by our system)
    case 'sending': return 'info';
    case 'sent': return 'info';
    // Sendgrid delivery event types
    // https://www.twilio.com/docs/sendgrid/for-developers/tracking-events/event#delivery-events
    case 'processed': return 'info';
    case 'dropped': return 'danger';
    case 'delivered': return 'success';
    case 'deferred': return 'warning';
    case 'bounce': return 'danger';
    default: return 'info';
  }
};

const SendgridEventForm = ({
  event,
}: SendgridEventFormProps) => {
  const history = useHistory();

  const handleViewOutboundEmail = () => {
    if (event?.attributes.outboundEmailUid) {
      history.push(`/admin/outbound-emails/${event.attributes.outboundEmailUid}`);
    }
  };

  const validationSchema = Yup.object().shape({
    attributes: Yup.object().shape({
      email: Yup.string().email().required(),
      event: Yup.string().required(),
      sgEventId: Yup.string().required(),
      sgMessageId: Yup.string().required(),
      smtpId: Yup.string().required(),
      sendgridTimestamp: Yup.string().required(),
      ip: Yup.string().required(),
      reason: Yup.string().nullable(),
      response: Yup.string().nullable(),
      status: Yup.string().nullable(),
      useragent: Yup.string().nullable(),
      url: Yup.string().nullable(),
      customArgs: Yup.string().nullable(),
      asmGroupId: Yup.string().nullable(),
      rawPayload: Yup.string().required(),
      attempt: Yup.number().nullable(),
      orgUid: Yup.string().required(),
      divisionUid: Yup.string().nullable(),
      outboundEmailUid: Yup.string().nullable(),
    }),
    id: Yup.string().required(),
    type: Yup.string().required(),
  });

  return (
    <Form
      displayName="SendgridEventForm"
      validateOnBlur
      enableReinitialize
      initialValues={{
        ...DEFAULT_VALUES,
        ...(event && event),
      }}
      validationSchema={validationSchema}
      onSubmit={() => {}} // No submit functionality - read-only form
      noValidate
    >
      {({ values }) => (
        <Card>
          <CardBody className="p-0">
            {event?.attributes.outboundEmailUid && (
              <div className="mb-3 p-3 border-bottom">
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleViewOutboundEmail}
                >
                  View Outbound Email Record
                </Button>
              </div>
            )}
            <fieldset>
              <CardTitle>Event Information</CardTitle>

              <Row form>
                <Col sm={6}>
                  <Field
                    name="attributes.email"
                    label="Email Address"
                    disabled
                  />
                </Col>

                <Col sm={6}>
                  <Field
                    name="attributes.event"
                    label="Event Type"
                    disabled
                    text={
                      <span className={`badge badge-${getBadgeClass(values.attributes?.event || '')}`}>
                        {values.attributes?.event || 'unknown'}
                      </span>
                    }
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={6}>
                  <Field
                    name="attributes.reason"
                    label="Reason"
                    type="textarea"
                    rows={2}
                    disabled
                  />
                </Col>

                <Col sm={6}>
                  <Field
                    name="attributes.response"
                    label="Response"
                    type="textarea"
                    rows={2}
                    disabled
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={4}>
                  <Field
                    name="attributes.status"
                    label="Status"
                    disabled
                  />
                </Col>

                <Col sm={4}>
                  <Field
                    name="attributes.ip"
                    label="IP Address"
                    disabled
                  />
                </Col>

                <Col sm={4}>
                  <Field
                    name="attributes.attempt"
                    label="Attempt"
                    type="number"
                    disabled
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.useragent"
                    label="User Agent"
                    type="textarea"
                    rows={2}
                    disabled
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.url"
                    label="URL"
                    disabled
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.customArgs"
                    label="Custom Arguments (JSON)"
                    type="textarea"
                    rows={6}
                    disabled
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    value={formatJson(values.attributes?.customArgs)}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.rawPayload"
                    label="Raw Payload (JSON)"
                    type="textarea"
                    rows={10}
                    disabled
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    value={formatJson(values.attributes?.rawPayload)}
                  />
                </Col>
              </Row>
            </fieldset>

            <fieldset>
              <CardTitle>Sendgrid Identifiers</CardTitle>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.sgEventId"
                    label="Sendgrid Event ID"
                    disabled
                    style={{ fontSize: '12px', wordBreak: 'break-all' }}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.sgMessageId"
                    label="Sendgrid Message ID"
                    disabled
                    style={{ fontSize: '12px', wordBreak: 'break-all' }}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.smtpId"
                    label="SMTP ID"
                    disabled
                    style={{ fontSize: '12px', wordBreak: 'break-all' }}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={6}>
                  <Field
                    name="attributes.sendgridTimestamp"
                    label="Timestamp"
                    disabled
                    value={formatTimestamp(values.attributes?.sendgridTimestamp || '')}
                  />
                </Col>

                <Col sm={6}>
                  <Field
                    name="attributes.asmGroupId"
                    label="ASM Group ID"
                    disabled
                  />
                </Col>
              </Row>
            </fieldset>

            <fieldset>
              <CardTitle>Related Records</CardTitle>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.orgUid"
                    label="Organization UID"
                    disabled
                    style={{ fontSize: '12px', wordBreak: 'break-all' }}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.divisionUid"
                    label="Division UID"
                    disabled
                    style={{ fontSize: '12px', wordBreak: 'break-all' }}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.outboundEmailUid"
                    label="Outbound Email UID"
                    disabled
                    style={{ fontSize: '12px', wordBreak: 'break-all' }}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={6}>
                  <Field
                    name="id"
                    label="Event Record ID"
                    disabled
                    style={{ fontSize: '12px', wordBreak: 'break-all' }}
                  />
                </Col>

                <Col sm={6}>
                  <Field
                    name="type"
                    label="Record Type"
                    disabled
                  />
                </Col>
              </Row>
            </fieldset>
          </CardBody>
        </Card>
      )}
    </Form>
  );
};

export default SendgridEventForm;
